import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AlarmRouteDevice, CallCenterDeliveryType, Recipient, RecipientType, Route, ServiceTime } from '../../../models/alarmroutes.model';
import { StaffModel } from '../../../models/staff.model';
import { IRoleSymbols } from 'app/models/permission.model';
import { NO_ROLE_SYMBOLS, recipientIconClasses, staffById } from 'app/shared/role-symbol/role-symbol';
import { CallCenter } from '../../../models/callcenter.model';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from '../../../services/api.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AlertTypePipe, DeviceIconClassPipe, DeviceTypePipe } from 'app/pipes/pipes';
import { CanPipe } from 'app/pipes/can.pipe';
import { Areas } from 'app/models/permission.model';

@Component({
    selector: 'alarm-route-info',
    templateUrl: './alarm-route-info.component.html',
    styleUrls: ['./alarm-route-info.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [CanPipe, CommonModule, TranslateModule, DeviceIconClassPipe, DeviceTypePipe, AlertTypePipe]
})
export class AlarmRouteInfoComponent implements OnInit, OnChanges {
  readonly Areas = Areas;


  constructor(private api: ApiService, private router: Router,) { }

  @Input() route: Route;
  @Input() callCenters: CallCenter[];
  /** Staff names and levels, for the recipients' role symbols */
  @Input() staffNames: StaffModel[] = [];
  /** Custom roles and who has them, for the recipients' role symbols */
  @Input() roleSymbols: IRoleSymbols = NO_ROLE_SYMBOLS;

  private staffLevelsSource: StaffModel[];
  private staffLevels = new Map<string, StaffModel>();
  @Output() routeDeleted = new EventEmitter<string>();
  @Output() closeInformationForm = new EventEmitter<void>();
  @Output() showEditForm = new EventEmitter<void>();
  @Output() activeTabIndexEmitter = new EventEmitter<number>();


  callCenterDeliveryTypeTranslationsMap: Map<CallCenterDeliveryType, string> = new Map([
    [CallCenterDeliveryType.UnableToDeliverAlarm, 'UNABLE_TO_DELIVER_ALARM'],
    [CallCenterDeliveryType.CallCenterFirst, 'CALL_CENTER_FIRST'],
    [CallCenterDeliveryType.CallCenterOnly, 'CALL_CENTER_ONLY'],
    [CallCenterDeliveryType.WhenUnableToDeliverEMACallCenterOnly, 'UNABLE_TO_DELIVER_EMA_CALL_CENTER_ONLY'],
    [CallCenterDeliveryType.WhenUnableToDeliverEMAAlarm, 'UNABLE_TO_DELIVER_EMA_ALARM'],
  ]);


  daysTranslationMap: Map<string, string> = new Map([
    ['mon', 'DAYOFWEEK_1'],
    ['tue', 'DAYOFWEEK_2'],
    ['wed', 'DAYOFWEEK_3'],
    ['thu', 'DAYOFWEEK_4'],
    ['fri', 'DAYOFWEEK_5'],
    ['sat', 'DAYOFWEEK_6'],
    ['sun', 'DAYOFWEEK_7'],
  ]);

  user: any;
  activeTabIndex: number = 0;
  activations: any;
  deleteConfirmation: boolean;
  showAdvancedSettings: boolean = false;

  emitActiveTabIndex(): void {
    this.activeTabIndexEmitter.emit(this.activeTabIndex);
  }

  getCallCenterInformation(id: string): CallCenter {
    return this.callCenters.find(callCenter => callCenter.id === id);
  }

  /** Role symbol of a staff member, team icon of a receiver team (same symbols as Staff Management) */
  recipientIcon(recipient: Recipient): string {
    if (this.staffLevelsSource !== this.staffNames) {
      this.staffLevelsSource = this.staffNames;
      this.staffLevels = staffById(this.staffNames);
    }
    return recipientIconClasses(recipient, this.staffLevels, this.roleSymbols);
  }

  recipientType(type: string): string {
    return type === RecipientType.User ? 'fal fa-user fa-fw ' : 'fal fa-users fa-fw ';
  }

  serviceTimeClass(serviceTime: ServiceTime) {
    return serviceTime.startTime === serviceTime.endTime ? 'fas fa-xs fa-circle text-danger' :
      'fas fa-xs fa-circle text-success'
  }


  enabledIndicator(enabled: boolean) {
    return enabled ?
      'fas fa-xs fa-circle text-success' : 'fas fa-xs fa-circle text-danger';
  }

  deleteRoute(): void {
    this.api.deleteAlarmRoute(this.route.id).subscribe(res => {
      this.routeDeleted.emit(this.route.id);
      this.closeInfoForm();
    }, err => {
      // this.translate.get(['ERROR']).subscribe(t => {
      //   this.editTeamFormError = t.ERROR;
      // })
    })
  }

  getCallCenterDeliveryType(callCenterFirst: boolean, keepAlarmOpenAfterCallCenter: boolean, emaBeforeCallCenter: boolean): CallCenterDeliveryType {
    if (!callCenterFirst && !keepAlarmOpenAfterCallCenter && !emaBeforeCallCenter) {
      return CallCenterDeliveryType.UnableToDeliverAlarm;
    }

    if (callCenterFirst && keepAlarmOpenAfterCallCenter && !emaBeforeCallCenter) {
      return CallCenterDeliveryType.CallCenterFirst;
    }

    if (callCenterFirst && !keepAlarmOpenAfterCallCenter && !emaBeforeCallCenter) {
      return CallCenterDeliveryType.CallCenterOnly;
    }

    if (callCenterFirst && !keepAlarmOpenAfterCallCenter && emaBeforeCallCenter) {
      return CallCenterDeliveryType.WhenUnableToDeliverEMACallCenterOnly;
    }

    if (callCenterFirst && keepAlarmOpenAfterCallCenter && emaBeforeCallCenter) {
      return CallCenterDeliveryType.WhenUnableToDeliverEMAAlarm;
    }
  }

  parseServiceTime(serviceTime: ServiceTime): string {
    serviceTime.startTime = this.trimTime(serviceTime.startTime);
    serviceTime.endTime = this.trimTime(serviceTime.endTime);
    if (serviceTime.startTime.startsWith('00:00') && serviceTime.endTime.startsWith('00:00')) {
      return 'NEVER';
    }

    if (serviceTime.startTime.startsWith('00:00') && serviceTime.endTime.startsWith('23:59')) {
      return 'ALWAYS';
    }

    return `${serviceTime.startTime} - ${serviceTime.endTime}`;
  }

  emaFeatureEnabled(): boolean {
    const features: [string] = JSON.parse(localStorage.getItem('features'));
    if (features) {
      return features.includes('EveronMobileApp');
    }
    return false;
  }

  // Track by functions for ngFor to prevent re-rendering of the whole list when a single item changes
  trackByDeviceId(_: number, device: AlarmRouteDevice) {
    return device.deviceId;
  }

  openDeviceTab(): void {
    const devices = this.route.devices;
    const sizeIncrement = 100;
    let batchSize = 25;
    let index = 0;

    // Add devices in batches to prevent UI from freezing when there are a lot of devices
    function addBatch(arrayToAdd: AlarmRouteDevice[]) {
      if (index < devices.length) {
        const nextBatch = devices.slice(index, index + batchSize);
        arrayToAdd.push(...nextBatch);
        index += batchSize;
        batchSize = sizeIncrement;
        setTimeout(() => addBatch(arrayToAdd), 0)
      }
    }

    this.route.devices = [];
    this.activeTabIndex = 1;

    addBatch(this.route.devices);
  }

  openDevicePage(deviceId: string): void {
    this.router.navigate(['device/' + deviceId]);
  }

  openPersonnelPage(staffId: string): void {
    this.router.navigate(['staff/' + staffId]);
  }

  openTeamPage(teamId: string): void {
    this.router.navigate(['teams/' + teamId]);
  }

  // 00:00:00 => 00:00
  trimTime(time: string): string {
    return time.substring(0, Math.max(time.length - 3, 5));
  }


  showEditingForm(): void {
    this.showEditForm.emit();
  }

  closeInfoForm(): void {
    this.closeInformationForm.emit();
  }

  ngOnInit() {
    this.user = JSON.parse(localStorage.getItem('user'));
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.activeTabIndex = 0;
    this.emitActiveTabIndex();
  }

  getDeviceIconClass(deviceType: string): string {
    return this.api.getDeviceIconClass(deviceType);
  }

}
